import { DeleteOutlined, EditOutlined, PersonOutlined } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import Box from '@mui/material/Box';
import { map, range } from 'lodash';
import { FilterContext, InfinitePagination } from 'ra-ui-materialui';
import { Fragment, useState, useImperativeHandle, forwardRef, useRef } from 'react';
import {
    FilterForm,
    InfiniteListBase,
    TextInput,
    useDelete,
    useNotify,
    usePermissions,
    useStore,
    useTranslate,
    WithListContext,
} from 'react-admin';
import { useNavigate, useParams } from 'react-router';
import {
    AVATARS,
    CLIENT_METADATA_AVATAR_TYPE_NAME,
    CLIENT_USERS_LIST_ROUTE,
    CLIENTS,
    CURRENT_CLIENT_NAVIGATION_STORE_KEY,
    NAVIGATION_ROUTE,
    ROLE_BIG_CREATE_SITE,
    ROLE_BIG_DELETE_CLIENT,
    ROLE_BIG_LINK_USER_TO_CLIENT,
    ROLE_BIG_READ_CLIENT,
    ROLE_BIG_UPDATE_CLIENT,
} from '@/utils/CONST';
import extractIdFromURI from '@/utils/id';
import { Avatar } from '@/utils/types';
import { composeUri } from '@/utils/compose';
import DeleteDialog from '@/components/common/delete-dialog/delete-dialog';
import AccordionItem from '@/components/navigation/navigation-items/accordion-item';
import AccordionItemSkeleton from '@/components/navigation/navigation-items/accordion-item-skeleton';
import SitesList from '@/components/navigation/navigation-items/sites-list';
import CreateClientDialog from '@/components/client/create-client-dialog';
import EditClientDialog from '@/components/client/edit-client-dialog';

interface ClientsListHandle {
    openCreateDialog: () => void;
}

interface ClientsListProps { }

const ClientsList = forwardRef<ClientsListHandle, ClientsListProps>((_, ref) => {
    const { clientId, siteId, placeId } = useParams();
    const navigate = useNavigate();
    const translate = useTranslate();
    const [deleteOne, { isLoading: isLoadingDelete }] = useDelete();
    const notify = useNotify();
    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [deleteId, setDeleteId] = useState<string>('');
    const [openCreateDialog, setOpenCreateDialog] = useState(false);
    const [openEditDialog, setOpenEditDialog] = useState(false);
    const [selectedClientId, setSelectedClientId] = useState<string>('');
    const sitesListRefs = useRef<Map<string, { openCreateDialog: (clientId: string) => void } | null>>(new Map());
    const { permissions } = usePermissions();
    const [currentClientNavigation, setCurrentClientNavigation] = useStore(
        CURRENT_CLIENT_NAVIGATION_STORE_KEY,
        undefined,
    );

    useImperativeHandle(ref, () => ({
        openCreateDialog: () => setOpenCreateDialog(true),
    }));

    const handleSelect = (id: string) => {
        navigate(`client/${id}`);
    };

    const navigateToEdit = (clientId: string) => {
        setSelectedClientId(clientId);
        setOpenEditDialog(true);
    };

    const navigateToCreateSite = (clientId: string) => {
        const clientUriId = composeUri(AVATARS, clientId);
        const sitesListRef = sitesListRefs.current.get(clientUriId);
        sitesListRef?.openCreateDialog(clientId);
    };

    const navigateToUsersList = (clientId: string) => {
        navigate(`${CLIENT_USERS_LIST_ROUTE}/${clientId}`);
    };
    //
    // const { groupAvatars, isLoading: isLoadingGroup } = useGetGroupAvatars({
    //     filters: { alphaId: 'g:default:clients' },
    // });
    // const defaultGroupId = useMemo(() => nth(groupAvatars)?.id, [groupAvatars]);

    const handleRemoveAvatar = (avatarUriId: string) => {
        if (!avatarUriId) return;

        const deletedClientId = extractIdFromURI(avatarUriId) ?? '';
        const isDeletingCurrentlyViewedClient = !!clientId && deletedClientId === clientId;

        deleteOne(
            CLIENTS,
            {
                id: avatarUriId,
                meta: {
                    requestType: 'update',
                    id: `${avatarUriId}/enable`,
                    data: {
                        enabled: false,
                        children: true,
                    },
                    realResource: AVATARS,
                },
            },
            {
                mutationMode: 'optimistic',
                onSuccess: () => {
                    notify('ra.notification.deleted', {
                        type: 'success',
                        messageArgs: { smart_count: 1 },
                    });
                    setOpenDeleteModal(false);
                    setDeleteId('');

                    if (isDeletingCurrentlyViewedClient) {
                        setCurrentClientNavigation(undefined);
                        navigate(`/${NAVIGATION_ROUTE}`);
                    }
                },
            },
        );
    };

    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateRows: 'auto 1fr',
                alignContent: 'start',
                height: '100%',
                overflowY: 'auto',
            }}
        >
            <InfiniteListBase
                resource={CLIENTS}
                perPage={30}
                sort={{
                    field: 'creationDate',
                    order: 'ASC',
                }}
                filter={{
                    // serialization_groups: ['read_group_users_group_avatars'],
                    'metadataAvatarType.name': CLIENT_METADATA_AVATAR_TYPE_NAME,
                    enabled: true,
                    // groupAvatars: defaultGroupId,
                }}
                storeKey={`clientList`}
                disableSyncWithLocation
                queryOptions={{
                    enabled: permissions?.includes(ROLE_BIG_READ_CLIENT),
                    meta: {
                        realResource: AVATARS,
                    }
                }}
            >
                <WithListContext
                    render={({ data, isLoading, total }) => (
                        <Fragment>
                            <Box
                                sx={{
                                    '& .RaFilterFormInput-spacer': {
                                        display: 'none',
                                    },
                                    '& form': {
                                        minHeight: 0,
                                    },
                                    '& form div': {
                                        width: '100%',
                                    },
                                    '& form .RaFilterForm-clearFix': {
                                        display: 'none',
                                    },
                                }}
                            >
                                <FilterContext.Provider
                                    value={[
                                        <TextInput
                                            name='name'
                                            placeholder={translate('main.searchClient')}
                                            label={false}
                                            source='name'
                                            helperText={false}
                                            sx={{
                                                '& .MuiInputBase-root': {
                                                    borderRadius: 0,
                                                },
                                                mt: '0 !important',
                                            }}
                                            alwaysOn
                                        />,
                                    ]}
                                >
                                    <FilterForm />
                                </FilterContext.Provider>
                            </Box>

                            <Box
                                sx={{
                                    height: '100%',
                                    overflowY: 'auto',
                                }}
                            >
                                {map(data, ({ id, name }: Avatar) => (
                                    <AccordionItem
                                        defaultExpanded={currentClientNavigation === id || extractIdFromURI(id) === clientId}
                                        onSelect={() => handleSelect(extractIdFromURI(id) ?? '')}
                                        selected={extractIdFromURI(id) === clientId && !siteId && !placeId}
                                        id={id}
                                        key={id}
                                        name={name}
                                        menuItems={[
                                            {
                                                label: translate('ra.action.edit'),
                                                icon: <EditOutlined />,
                                                onClick: () => navigateToEdit(extractIdFromURI(id) as string),
                                                disabled: !permissions?.includes(ROLE_BIG_UPDATE_CLIENT),
                                            },
                                            {
                                                label: translate('ra.action.delete'),
                                                icon: <DeleteOutlined />,
                                                onClick: () => {
                                                    setOpenDeleteModal(true);
                                                    setDeleteId(id);
                                                },
                                                disabled: !permissions?.includes(ROLE_BIG_DELETE_CLIENT),
                                            },
                                            {
                                                label: translate('buttons.add_site'),
                                                icon: <AddIcon />,
                                                onClick: () =>
                                                    navigateToCreateSite(extractIdFromURI(id) as string),
                                                disabled: !permissions?.includes(ROLE_BIG_CREATE_SITE),
                                            },
                                            {
                                                label: translate('main.navigation.users'),
                                                icon: <PersonOutlined />,
                                                onClick: () =>
                                                    navigateToUsersList(extractIdFromURI(id) as string),
                                                test: 'user-list',
                                                disabled: !permissions?.includes(ROLE_BIG_LINK_USER_TO_CLIENT),
                                            },
                                        ]}
                                        withLogo
                                    >
                                        <SitesList 
                                            clientUriId={id} 
                                            ref={(node) => {
                                                if (node) {
                                                    sitesListRefs.current.set(id, node);
                                                } else {
                                                    sitesListRefs.current.delete(id);
                                                }
                                            }}
                                        />
                                    </AccordionItem>
                                ))}

                                {isLoading && range(3).map((index) => <AccordionItemSkeleton key={index} />)}

                                {data && total !== undefined && data.length < total && <InfinitePagination />}


                            </Box>
                        </Fragment>
                    )}
                />
            </InfiniteListBase>

            {openCreateDialog && (
                <CreateClientDialog
                    open={openCreateDialog}
                    onClose={() => setOpenCreateDialog(false)}
                />
            )}

            {openEditDialog && selectedClientId && (
                <EditClientDialog
                    open={openEditDialog}
                    onClose={() => {
                        setOpenEditDialog(false);
                        setSelectedClientId('');
                    }}
                    clientId={selectedClientId}
                />
            )}

            {openDeleteModal && (
                <DeleteDialog
                    isLoading={isLoadingDelete}
                    onClose={() => setOpenDeleteModal(false)}
                    onClick={() => handleRemoveAvatar(deleteId)}
                />
            )}
        </Box>
    );
});

ClientsList.displayName = 'ClientsList';

export default ClientsList;
